import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatSettingValue, SCOPE_LABEL, validateSettingValue, type SettingScope } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select } from '../components/ui';

/** INT-ADM-01: every key in INTENTS §6, its scopes and who may change it, current values, edits and the effective-value preview. */

interface StoredValue {
  scope: SettingScope;
  scopeId: string;
  label: string;
  value: unknown;
  valid: boolean;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}
interface Descriptor {
  key: string;
  description: string;
  default: unknown;
  scopes: SettingScope[];
  editorLabels: string[];
  kind: 'int' | 'bool' | 'enum' | 'list' | 'other';
  min?: number;
  max?: number;
  options?: string[];
  unit?: string;
  group: string;
  canEdit: boolean;
  values: StoredValue[];
}
interface Targets {
  markets: { id: string; code: string; name: string }[];
  accounts: { id: string; code: string; name: string; marketId: string; currency: string }[];
  trades: { id: string; accountId: string; label: string }[];
  setups: { id: string; marketId: string; label: string }[];
  users: { id: string; name: string }[];
}
interface Effective {
  context: Record<string, { code?: string; label?: string; name?: string }>;
  values: { key: string; value: unknown; source: { scope: string; scopeId: string; label: string | null } | null; chain: { scope: string; scopeId: string | null; label: string | null; value: unknown; status: string }[] }[];
}
interface PolicyRow {
  id: string;
  code: string;
  name: string;
  market: string;
  currency: string;
  policy: { id: string; name: string; sharedWith: number; isTemplate: boolean; autoMaxRiskPct: string; hardMaxRiskPct: string; maxOpenRiskPct: string; maxOpenTrades: number; dailyLossLimitPct: string; minRewardRisk: string; requireStopLoss: boolean; requireLinkedAnalysis: boolean };
}

const fmt = (key: string, v: unknown) => formatSettingValue(key, v);
const ORDER: SettingScope[] = ['GLOBAL', 'MARKET', 'ACCOUNT', 'TRADE', 'SETUP', 'USER'];

function targetOptions(scope: SettingScope, t: Targets | undefined): { id: string; label: string }[] {
  if (!t) return [];
  switch (scope) {
    case 'MARKET':
      return t.markets.map((m) => ({ id: m.id, label: `${m.code} · ${m.name}` }));
    case 'ACCOUNT':
      return t.accounts.map((a) => ({ id: a.id, label: `${a.code} · ${a.name}` }));
    case 'TRADE':
      return t.trades.map((x) => ({ id: x.id, label: x.label }));
    case 'SETUP':
      return t.setups.map((x) => ({ id: x.id, label: x.label }));
    case 'USER':
      return t.users.map((u) => ({ id: u.id, label: u.name }));
    default:
      return [{ id: '*', label: 'Whole floor' }];
  }
}

function ValueInput({ d, value, onChange, id }: { d: Descriptor; value: string; onChange: (v: string) => void; id: string }) {
  if (d.kind === 'bool') return <Segmented label="Value" options={[{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]} value={value} onChange={onChange} />;
  if (d.kind === 'enum') return <Segmented label="Value" options={(d.options ?? []).map((o) => ({ value: o, label: o }))} value={value} onChange={onChange} />;
  return (
    <Field label={d.kind === 'list' ? 'Value (comma-separated)' : `Value${d.unit ? ` in ${d.unit === 'per_minute' ? 'calls per minute' : d.unit}` : ''}`} hint={d.kind === 'int' && d.min !== undefined ? `From ${d.min} to ${d.max}${d.key === 'PROGRESS_REPORT_INTERVAL' ? ' (30 min to 24 h)' : ''}` : undefined}>
      <Input id={id} mono={d.kind === 'int'} inputMode={d.kind === 'int' ? 'numeric' : undefined} value={value} onChange={(e) => onChange(e.target.value)} className={d.kind === 'int' ? 'max-w-[200px]' : undefined} />
    </Field>
  );
}

const parseInput = (d: Descriptor, raw: string): unknown => {
  if (d.kind === 'int') return raw.trim() === '' || !/^-?\d+(\.\d+)?$/.test(raw.trim()) ? raw : Number(raw);
  if (d.kind === 'bool') return raw === 'true';
  if (d.kind === 'list') return raw.split(/[,\s]+/).filter(Boolean);
  return raw;
};
const toInput = (d: Descriptor, v: unknown) => (Array.isArray(v) ? v.join(', ') : v === undefined || v === null ? '' : String(v));

function Editor({ d, targets, onDone }: { d: Descriptor; targets: Targets | undefined; onDone: (msg: string) => void }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<SettingScope>(d.scopes.includes('GLOBAL') ? 'GLOBAL' : d.scopes[0]!);
  const [scopeId, setScopeId] = useState('*');
  const existing = d.values.find((v) => v.scope === scope && v.scopeId === scopeId);
  const [raw, setRaw] = useState(toInput(d, existing?.value ?? d.default));
  const value = parseInput(d, raw);
  const check = validateSettingValue(d.key, value);
  const opts = targetOptions(scope, targets);
  const save = useMutation({
    mutationFn: () => api<{ rescheduled: number }>('/settings', { method: 'PUT', body: { key: d.key, scope, scopeId: scope === 'GLOBAL' ? '*' : scopeId, value } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['settings-console'] });
      void qc.invalidateQueries({ queryKey: ['effective'] });
      onDone(`Saved.${r.rescheduled ? ` ${r.rescheduled} pending report${r.rescheduled === 1 ? '' : 's'} rescheduled.` : ''} New deadlines use it straight away; the worker picks it up within 30 seconds.`);
    },
  });
  const pickScope = (s: SettingScope) => {
    setScope(s);
    setScopeId(s === 'GLOBAL' ? '*' : '');
  };
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line2 bg-raised p-3.5">
      <div className="flex flex-wrap gap-3">
        <Field label="Applies to">
          <Select value={scope} onChange={(e) => pickScope(e.target.value as SettingScope)} className="min-w-[160px]">
            {d.scopes.map((s) => (
              <option key={s} value={s}>
                {s === 'GLOBAL' ? 'Whole floor' : `One ${SCOPE_LABEL[s].toLowerCase()}`}
              </option>
            ))}
          </Select>
        </Field>
        {scope !== 'GLOBAL' && (
          <Field label={`Which ${SCOPE_LABEL[scope].toLowerCase()}`} className="min-w-[220px] flex-1">
            <Select
              value={scopeId}
              onChange={(e) => {
                setScopeId(e.target.value);
                const ex = d.values.find((v) => v.scope === scope && v.scopeId === e.target.value);
                if (ex) setRaw(toInput(d, ex.value));
              }}
            >
              <option value="">Choose…</option>
              {opts.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      <ValueInput d={d} value={raw} onChange={setRaw} id={`v-${d.key}`} />
      {!check.ok && raw !== '' && <p className="m-0 text-sm text-warn">{check.message}</p>}
      {existing && <p className="m-0 text-xs text-muted">Currently {fmt(d.key, existing.value)} here — saving replaces it.</p>}
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={!check.ok || (scope !== 'GLOBAL' && !scopeId) || save.isPending}>
          Save {d.key === 'PROGRESS_REPORT_INTERVAL' ? 'interval' : 'value'}
        </Button>
        <Button variant="ghost" onClick={() => onDone('')}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function SettingRow({ d, targets, tz }: { d: Descriptor; targets: Targets | undefined; tz: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState('');
  const clear = useMutation({
    mutationFn: (v: StoredValue) => api(`/settings?key=${d.key}&scope=${v.scope}&scopeId=${encodeURIComponent(v.scopeId)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings-console'] });
      void qc.invalidateQueries({ queryKey: ['effective'] });
      setMsg('Cleared. The next less specific value applies.');
    },
  });
  const values = [...d.values].sort((a, b) => ORDER.indexOf(a.scope) - ORDER.indexOf(b.scope) || a.label.localeCompare(b.label));
  return (
    <div className="flex flex-col gap-2.5 border-t border-[#1a1f27] px-4 py-3.5 first:border-t-0" id={`setting-${d.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-[240px] flex-1 flex-col gap-1">
          <h3 className="m-0 font-mono text-[13px] font-semibold text-ink">{d.key}</h3>
          <p className="m-0 text-sm text-sub">{d.description}</p>
          <p className="m-0 text-xs text-muted">
            Default {fmt(d.key, d.default)} · set per {d.scopes.map((s) => SCOPE_LABEL[s].toLowerCase()).join(', ')} · changed by {d.editorLabels.join(' or ')}
          </p>
        </div>
        {d.canEdit && !editing && (
          <Button
            onClick={() => {
              setEditing(true);
              setMsg('');
            }}
            aria-label={`Change ${d.key}`}
          >
            Change
          </Button>
        )}
      </div>
      {values.length > 0 && (
        <ul aria-label={`Values set for ${d.key}`} className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {values.map((v) => (
            <li key={`${v.scope}:${v.scopeId}`} className={cx('flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-3 text-sm', v.valid ? 'border-line2 bg-panel' : 'border-[#6a2a27] bg-down-bg')} title={`Set by ${v.updatedBy?.name ?? 'unknown'} ${fmtTime(v.updatedAt, tz, true)}`}>
              <span className="text-muted">{v.scope === 'GLOBAL' ? 'Whole floor' : v.label}:</span>
              <span className="font-semibold">{fmt(d.key, v.value)}</span>
              {!v.valid && <span className="text-xs text-down">invalid, ignored</span>}
              {d.canEdit ? (
                <button type="button" aria-label={`Clear ${d.key} for ${v.scope === 'GLOBAL' ? 'the whole floor' : v.label}`} onClick={() => clear.mutate(v)} className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink">
                  ×
                </button>
              ) : (
                <span className="w-1" />
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <Editor
          d={d}
          targets={targets}
          onDone={(m) => {
            setEditing(false);
            setMsg(m);
          }}
        />
      )}
      <ErrorText error={clear.error ? errorText(clear.error) : null} />
      {msg && (
        <p role="status" className="m-0 text-sm text-up">
          {msg}
        </p>
      )}
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = { used: 'used', shadowed: 'overridden', invalid: 'invalid, ignored', not_set: 'not set', no_target: 'not chosen' };

function EffectivePreview({ targets, descriptors }: { targets: Targets | undefined; descriptors: Descriptor[] }) {
  const [sel, setSel] = useState({ marketId: '', accountId: '', tradeId: '' });
  const [focus, setFocus] = useState('PROGRESS_REPORT_INTERVAL');
  const params = new URLSearchParams(Object.entries(sel).filter(([, v]) => v));
  const eff = useQuery({ queryKey: ['effective', params.toString()], queryFn: () => api<Effective>(`/settings/effective?${params.toString()}`) });
  const accounts = (targets?.accounts ?? []).filter((a) => !sel.marketId || a.marketId === sel.marketId);
  const trades = (targets?.trades ?? []).filter((t) => !sel.accountId || t.accountId === sel.accountId);
  const focused = eff.data?.values.find((v) => v.key === focus);
  const scopeLabel = (s: string) => (s === 'DEFAULT' ? 'Default' : s === 'GLOBAL' ? 'Whole floor' : SCOPE_LABEL[s as SettingScope] ?? s);
  return (
    <Card tone="accent" className="flex flex-col">
      <CardHeader title="Effective value" />
      <div className="flex flex-col gap-3 p-4">
        <p className="m-0 text-sm text-sub">Pick a market, account or trade to see what each setting resolves to and where the value comes from.</p>
        <div className="grid gap-2.5 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
          <Field label="Market">
            <Select value={sel.marketId} onChange={(e) => setSel({ marketId: e.target.value, accountId: '', tradeId: '' })}>
              <option value="">Any</option>
              {targets?.markets.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Account">
            <Select value={sel.accountId} onChange={(e) => setSel({ ...sel, accountId: e.target.value, tradeId: '' })}>
              <option value="">Any</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Open trade">
            <Select value={sel.tradeId} onChange={(e) => setSel({ ...sel, tradeId: e.target.value })}>
              <option value="">Any</option>
              {trades.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <ErrorText error={eff.error ? errorText(eff.error) : null} />
        {focused && (
          <div className="flex flex-col gap-2 rounded-lg bg-raised p-3" aria-live="polite">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-mono text-[13px] font-semibold">{focused.key}</span>
              <span className="text-lg font-semibold" data-testid="effective-value">
                {fmt(focused.key, focused.value)}
              </span>
            </div>
            <span className="text-sm text-sub">
              {focused.source ? (
                <>
                  From <strong className="text-ink">{focused.source.scope === 'GLOBAL' ? 'the whole-floor value' : `${SCOPE_LABEL[focused.source.scope as SettingScope]?.toLowerCase() ?? ''} ${focused.source.label}`}</strong>
                </>
              ) : (
                'The default — nothing is set for this context'
              )}
            </span>
            <ol className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
              {focused.chain.map((c, i) => (
                <li key={i} className={cx('flex justify-between gap-2', c.status === 'used' ? 'font-semibold text-ink' : 'text-muted')}>
                  <span>
                    {i + 1}. {scopeLabel(c.scope)}
                    {c.label && c.scope !== 'GLOBAL' && c.scope !== 'DEFAULT' ? ` ${c.label}` : ''}
                  </span>
                  <span>
                    {c.value !== undefined && c.value !== null ? fmt(focused.key, c.value) : ''} <span className="text-xs">{c.status === 'used' ? '✓ used' : STATUS_TEXT[c.status]}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-2 pr-2 font-medium">Setting</th>
                <th className="py-2 pr-2 font-medium">Value</th>
                <th className="py-2 font-medium">From</th>
              </tr>
            </thead>
            <tbody>
              {eff.data?.values.map((v) => (
                <tr key={v.key} className={cx('cursor-pointer border-t border-[#1a1f27]', v.key === focus && 'bg-accent-bg')} onClick={() => setFocus(v.key)}>
                  <td className="py-1.5 pr-2">
                    <button type="button" className="cursor-pointer text-left font-mono text-xs text-ink2" onClick={() => setFocus(v.key)}>
                      {v.key}
                    </button>
                  </td>
                  <td className="py-1.5 pr-2 font-semibold">{fmt(v.key, v.value)}</td>
                  <td className="py-1.5 text-xs text-sub">{v.source ? (v.source.scope === 'GLOBAL' ? 'Whole floor' : v.source.label) : <span className="text-muted">default</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!descriptors.length && <Empty>Loading…</Empty>}
        </div>
      </div>
    </Card>
  );
}

const POLICY_FIELDS: { key: keyof PolicyRow['policy']; label: string; kind: 'pct' | 'int' | 'ratio' | 'bool' }[] = [
  { key: 'autoMaxRiskPct', label: 'Auto-approve up to', kind: 'pct' },
  { key: 'hardMaxRiskPct', label: 'Hard max per trade', kind: 'pct' },
  { key: 'maxOpenRiskPct', label: 'Max open risk', kind: 'pct' },
  { key: 'maxOpenTrades', label: 'Max open trades', kind: 'int' },
  { key: 'dailyLossLimitPct', label: 'Daily loss limit', kind: 'pct' },
  { key: 'minRewardRisk', label: 'Min reward : risk', kind: 'ratio' },
  { key: 'requireStopLoss', label: 'Stop loss required', kind: 'bool' },
  { key: 'requireLinkedAnalysis', label: 'Linked analysis required', kind: 'bool' },
];
const showPolicy = (f: (typeof POLICY_FIELDS)[number], v: unknown) => (f.kind === 'bool' ? (v ? 'Yes' : 'No') : f.kind === 'pct' ? `${Number(v)} %` : String(v));

function PolicyEditor({ row, onDone }: { row: PolicyRow; onDone: (m: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Record<string, string | boolean>>(() => Object.fromEntries(POLICY_FIELDS.map((x) => [x.key, x.kind === 'bool' ? (row.policy[x.key] as boolean) : String(row.policy[x.key])])));
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      for (const x of POLICY_FIELDS) {
        const now = f[x.key]!;
        const was = x.kind === 'bool' ? row.policy[x.key] : String(row.policy[x.key]);
        if (now !== was) body[x.key] = x.kind === 'int' ? Number(now) : now;
      }
      return api<{ copied: boolean }>(`/accounts/${row.id}/risk-policy`, { method: 'PATCH', body });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['risk-policies'] });
      onDone(r.copied ? `Saved. ${row.code} now has its own policy; other accounts keep theirs.` : 'Saved.');
    },
  });
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line2 bg-raised p-3.5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {POLICY_FIELDS.map((x) =>
          x.kind === 'bool' ? (
            <label key={x.key} className="flex cursor-pointer items-center gap-2 self-end pb-2.5 text-sm">
              <input type="checkbox" className="h-[18px] w-[18px]" checked={f[x.key] as boolean} onChange={(e) => setF({ ...f, [x.key]: e.target.checked })} />
              {x.label}
            </label>
          ) : (
            <Field key={x.key} label={`${x.label}${x.kind === 'pct' ? ' (%)' : ''}`}>
              <Input mono inputMode="decimal" value={f[x.key] as string} onChange={(e) => setF({ ...f, [x.key]: e.target.value })} />
            </Field>
          ),
        )}
      </div>
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <div className="flex gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save policy
        </Button>
        <Button variant="ghost" onClick={() => onDone('')}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function RiskPolicies() {
  const q = useQuery({ queryKey: ['risk-policies'], queryFn: () => api<{ canEdit: boolean; accounts: PolicyRow[] }>('/risk-policies') });
  const [editing, setEditing] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  return (
    <Card>
      <CardHeader title="Account risk policies" right={<span className="text-xs text-muted">{q.data?.canEdit ? 'You can change these' : 'Changed by Risk Managers'}</span>} />
      <ErrorText error={q.error ? errorText(q.error) : null} />
      {q.data?.accounts.map((a) => (
        <div key={a.id} className="flex flex-col gap-2.5 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">
              {a.code} <span className="font-normal text-muted">· {a.market} · {a.currency} · {a.policy.isTemplate ? 'default policy' : a.policy.sharedWith > 0 ? `${a.policy.name}, shared with ${a.policy.sharedWith} more` : a.policy.name}</span>
            </span>
            {q.data.canEdit && editing !== a.id && (
              <Button
                onClick={() => {
                  setEditing(a.id);
                  setMsg('');
                }}
                aria-label={`Edit risk policy for ${a.code}`}
              >
                Edit
              </Button>
            )}
          </div>
          {editing === a.id ? (
            <PolicyEditor
              row={a}
              onDone={(m) => {
                setEditing(null);
                setMsg(m);
              }}
            />
          ) : (
            <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
              {POLICY_FIELDS.map((x) => (
                <div key={x.key} className="flex justify-between gap-2 sm:flex-col sm:justify-start sm:gap-0">
                  <dt className="text-xs text-muted">{x.label}</dt>
                  <dd className="m-0 font-mono">{showPolicy(x, a.policy[x.key])}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ))}
      {msg && (
        <p role="status" className="m-0 px-4 pb-3 text-sm text-up">
          {msg}
        </p>
      )}
    </Card>
  );
}

export function SettingsConsole() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const q = useQuery({ queryKey: ['settings-console'], queryFn: () => api<{ settings: Descriptor[]; orphans: (StoredValue & { key: string })[] }>('/settings') });
  const targets = useQuery({ queryKey: ['settings-targets'], queryFn: () => api<Targets>('/settings/targets'), staleTime: 60_000 });
  const [tab, setTab] = useState<'settings' | 'policies'>('settings');
  const groups = useMemo(() => {
    const out: { title: string; items: Descriptor[] }[] = [];
    for (const d of q.data?.settings ?? []) {
      const g = out.find((x) => x.title === d.group) ?? (out.push({ title: d.group, items: [] }), out.at(-1)!);
      g.items.push(d);
    }
    return out;
  }, [q.data]);
  const isAdmin = hasRole(me.data?.roles, 'ADMIN');

  return (
    <>
      <PageHeader
        title="Settings console"
        sub={tab === 'policies' ? "Each account's risk limits, used by the automatic risk check. Risk Managers change them; every change is in the audit log." : 'Floor rules and where they apply. The most specific value wins: trade → account → market → whole floor → default.'}
        right={<Segmented label="Section" options={[{ value: 'settings', label: 'Floor settings' }, { value: 'policies', label: 'Risk policies' }]} value={tab} onChange={setTab} />}
      />
      <ErrorText error={q.error ? errorText(q.error) : null} />
      {!isAdmin && tab === 'settings' && <p className="m-0 text-sm text-sub">You can see every setting. {hasRole(me.data?.roles, 'MASTER_TRADER') ? 'As a Master Trader you can change the progress report interval.' : 'Only Admins change these.'}</p>}
      {tab === 'policies' ? (
        <RiskPolicies />
      ) : (
        <div className="grid items-start gap-3.5 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)]">
          <div className="flex min-w-0 flex-col gap-3.5">
            {groups.map((g) => (
              <Card key={g.title}>
                <CardHeader title={g.title} />
                {g.items.map((d) => (
                  <SettingRow key={d.key} d={d} targets={targets.data} tz={tz} />
                ))}
              </Card>
            ))}
            {!!q.data?.orphans.length && (
              <Card tone="warn" className="p-4">
                <h2 className="m-0 text-[15px] font-semibold">Values for settings that no longer exist</h2>
                {q.data.orphans.map((o) => (
                  <p key={`${o.key}:${o.scope}:${o.scopeId}`} className="m-0 font-mono text-sm text-sub">
                    {o.key} · {o.label}: {JSON.stringify(o.value)}
                  </p>
                ))}
              </Card>
            )}
            {q.isLoading && <Card className="p-4 text-sub">Loading…</Card>}
          </div>
          <div className="flex min-w-0 flex-col gap-3.5 lg:sticky lg:top-4">
            <EffectivePreview targets={targets.data} descriptors={q.data?.settings ?? []} />
            <Pill tone="neutral" className="self-start whitespace-normal">
              Every change is recorded in the audit log with the old and new value.
            </Pill>
          </div>
        </div>
      )}
    </>
  );
}
